import { globalObservability } from '@opendoor/observability/slim';

import { getAnonymousId } from '../shared/trackers';
import {
  EvaluateExperimentAssignmentDocument,
  TrackExperimentImpressionDocument,
} from './__generated__/experiment-assignments.zeus';
import type { EvaluateExperimentAssignmentMutation } from './__generated__/experiment-assignments.zeus';
import { requestZeusFromBrowser } from './zeusBrowserClient';

const Sentry = globalObservability.getSentryClient();

type EvaluatedPayload = Extract<
  EvaluateExperimentAssignmentMutation['evaluateExperimentVariant'],
  { __typename: 'EvaluateExperimentVariantPayload' }
>;

// `value` is null when nothing is served for the key (environment off, missing
// key). `variationId` is set only for experiment-backed assignments, so a
// feature default or a force rule serves a value without one.
export type ExperimentAssignment = Pick<EvaluatedPayload['assignment'], 'value' | 'variationId'>;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Zeus validates the grain as a UUID; legacy Segment cookies can carry a
// JSON-quoted value, so normalize rather than hard-failing those visitors.
const uuidAnonymousId = (): string | undefined => {
  const raw = getAnonymousId()?.replaceAll('"', '');
  return raw && UUID_PATTERN.test(raw) ? raw : undefined;
};

type Identity = { anonymousId: string | null; customerId: string | null };

const buildIdentity = (customerId?: string): Identity | null => {
  const anonymousId = uuidAnonymousId();
  if (!anonymousId && !customerId) {
    return null;
  }
  return { anonymousId: anonymousId ?? null, customerId: customerId ?? null };
};

// Resolves undefined on request failure or missing identity; a resolved
// assignment with a null value means the server serves nothing for the key.
export async function getExperimentAssignment(
  key: string,
  customerId?: string,
): Promise<ExperimentAssignment | undefined> {
  const identity = buildIdentity(customerId);
  if (!identity) {
    return undefined;
  }
  try {
    const result = await requestZeusFromBrowser(EvaluateExperimentAssignmentDocument, {
      input: { grain: { customer: identity }, key, autoImpression: false },
    });
    const evaluated = result.evaluateExperimentVariant;
    if (evaluated.__typename !== 'EvaluateExperimentVariantPayload') {
      // Zeus returns rejections as union members on an HTTP 200, so nothing
      // throws. Unreported, a schema or auth regression sends every visitor to
      // fail-closed control with no signal anywhere.
      Sentry.captureException?.(
        new Error(
          `Experiment assignment rejected for ${key}: ${evaluated.code} ${evaluated.message}`,
        ),
      );
      return undefined;
    }
    return {
      value: evaluated.assignment.value,
      variationId: evaluated.assignment.variationId,
    };
  } catch (e) {
    Sentry.captureException?.(e);
    return undefined;
  }
}

// Records the exposure with the Variant service; assignment is unchanged.
export async function recordExperimentImpression(
  key: string,
  assignment: { variant: string; variationId: number | null },
  customerId?: string,
): Promise<void> {
  const identity = buildIdentity(customerId);
  if (!identity) {
    return;
  }
  try {
    const result = await requestZeusFromBrowser(TrackExperimentImpressionDocument, {
      input: {
        grain: { customer: identity },
        key,
        variant: assignment.variant,
        variationId: assignment.variationId,
        isIdempotent: true,
      },
    });
    const outcome = result.trackExperimentImpression;
    if (outcome.__typename !== 'CommandResult') {
      Sentry.captureException?.(
        new Error(`Experiment impression rejected for ${key}: ${outcome.code} ${outcome.message}`),
      );
    }
  } catch (e) {
    Sentry.captureException?.(e);
  }
}
